import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import Ipms from '../../images/ipms.svg';
import { API } from '../../apis/api';
import { API_RESOURCE_URLS, ASN_STATUS } from '../../constants';
import './InformationRadiator.css';
import { useAjaxCallbackWithIntervals } from '../../hooks';
import {
  getFormattedAsn,
  splitDescription,
  compareByDateAndSerialNumber,
} from '../../utils/tableUtils';
import { usePopupManager } from '../../providers/PopupManager/PopupManager';
import { usePlantAndLinesStore } from '../../providers/PlantAndLinesStore/PlantAndLinesStore';
import LineDropdownSelector from '../LineDropdownSelector/LineDropdownSelector';
import { Link } from 'react-router-dom';
import { buildErrorMessage } from '../../apis/calls';

const InformationRadiator = () => {
  
  const DROPPED_ROWS_COUNT = 30;

  const [plant] = usePlantAndLinesStore().plantState;
  const [line] = usePlantAndLinesStore().lineState;

  const latestLineValueRef = useRef(line.value);
  const latestPlantValueRef = useRef(plant.value);

  const { showInternalError } = usePopupManager();

  const [droppedRowsData, setDroppedRowsData] = useState({
    data: [],
    loading: false,
  });

  const [readyForDropRowsData, setReadyForDropRowsData] = useState({
    data: [],
    loading: false,
  });
  const READY_FOR_DROP_ROWS_COUNT = readyForDropRowsData.data.length;

  const getAsnsByStatusForCount = async (status, count, optionalParams) => {
    if (plant.value == null) return;
    if (line.value == null) return;

    const response = await API.get(
      API_RESOURCE_URLS.getDroppingUpdate(
        plant.value,
        line.value,
        status,
        count,
      ),
    );

    return response.data;
  };

  const prepareLastDroppedAsnRows = (asns, requiredNoOfRows) => {
    let sortDateByDesc =
      asns &&
      asns.sort((a, b) => {
        if (
          a.dropTimestamp != null &&
          a !== undefined &&
          b.dropTimestamp != null &&
          b !== undefined
        )
          return (
            new Date(...b?.dropTimestamp.split('/').reverse()) -
            new Date(...a?.dropTimestamp.split('/').reverse())
          );
        else return [];
      });

    let position = 0;
    const addPositionValue = sortDateByDesc?.map(FormatedValue => {
      position = position + 1;
      return { ...FormatedValue, position: position };
    });

    const positionToAsnMap = {};

    addPositionValue &&
      addPositionValue.forEach(asn => (positionToAsnMap[asn.position] = asn));

    const rowsInAscendingOrder = [...Array(requiredNoOfRows).keys()].map(
      (_, index) => {
        return positionToAsnMap[index + 1];
      },
    );
    return rowsInAscendingOrder;
  };

  const loadLastDroppedAsn = async () => {
    setDroppedRowsData(prevState => ({ ...prevState, loading: true }));
    try {
      const noOfRows = 30;

      const asns = await getAsnsByStatusForCount(ASN_STATUS.DROPPED, noOfRows);
      if (
        plant.value !== latestPlantValueRef.current ||
        line.value !== latestLineValueRef.current
      )
        return setDroppedRowsData(prevState => ({
          ...prevState,
          loading: false,
        }));

      const positionallyFormattedRows = prepareLastDroppedAsnRows(
        asns,
        noOfRows,
      );

      setDroppedRowsData({
        data: positionallyFormattedRows,
        loading: false,
      });
    } catch (err) {
      console.error(
        'failed to load last dropped asn, err:' + buildErrorMessage(err),
      );
      setDroppedRowsData({
        data: [],
        loading: false,
      });
      showInternalError();
    }
  };

  const prepareReadyForDropRows = (data, requiredNoOfRows) => {
    const positionToAsnMap = {};
    data && data.forEach(asn => (positionToAsnMap[asn.position] = asn));
    const rowsInDescendingOrder = [...Array(requiredNoOfRows).keys()]
      .map((_, index) => positionToAsnMap[index + 1] || { position: index + 1 })
      .sort((a, b) => b.position - a.position);
    return rowsInDescendingOrder;
  };

  const loadFirstReadyForDroppedAsn = async () => {
    setReadyForDropRowsData(prevState => ({ ...prevState, loading: true }));
    try {
      const noOfRows = 15;
      const asns = await getAsnsByStatusForCount(
        ASN_STATUS.READY_FOR_DROP,
        noOfRows,
      );
      const validAsns = Array.isArray(asns) ? asns : [];
      const noOfRowsForAsn = validAsns.length;

      if (
        plant.value !== latestPlantValueRef.current ||
        line.value !== latestLineValueRef.current
      )
        return setReadyForDropRowsData(prevState => ({
          ...prevState,
          loading: false,
        }));

      const sortedData = asns?.sort((a, b) =>
        compareByDateAndSerialNumber(a, b, 'productionDate', 'sequence'),
      );

      let position = 0;
      const addPositionValue = sortedData?.map(FormatedValue => {
        position = position + 1;
        return { ...FormatedValue, position: position };
      });

      const positionallyFormattedRows = prepareReadyForDropRows(
        addPositionValue,
        noOfRowsForAsn,
      );
      setReadyForDropRowsData({
        data: positionallyFormattedRows,
        loading: false,
      });
    } catch (err) {
      console.error(
        'failed to load last ready for drop asn, err:' + buildErrorMessage(err),
      );
      setReadyForDropRowsData({
        data: [],
        loading: false,
      });
      showInternalError();
    }
  };

  const loadRows = () => {
    return loadFirstReadyForDroppedAsn().then(() => loadLastDroppedAsn());
  };

  useEffect(() => {
    latestLineValueRef.current = line.value;
    latestPlantValueRef.current = plant.value;
  }, [plant.value, line.value]);

  const lastRefreshedDate = useAjaxCallbackWithIntervals(
    droppedRowsData.loading || readyForDropRowsData.loading,
    false,
    loadRows,
    line.value,
    plant.value,
  );

  const getPaddedPositionString = positionIndex =>
    String(positionIndex).padStart(2, '0');

  const renderRow = index => {
    const row = droppedRowsData.data[index];

    const [descriptionHead, descriptionTail] = splitDescription(
      row && row.materialDescription,
    );

    const position = row ? row.position : index + 1;

    return (
      <tr key={index}>
        <td width="7%" height="30" align="center" valign="middle">
          {index === 0 ? 'Station' : <>&nbsp;</>}
        </td>
        <td width="5%" align="center" valign="middle">
          {getPaddedPositionString(position)}
        </td>
        <td
          width="13%"
          align="center"
          valign="middle"
          className="asn"
          data-testid={`select-Dropped-asn-${index}`}
        >
          {getFormattedAsn(row)}
        </td>
        <td
          align="left"
          valign="middle"
          width="56%"
          style={{ paddingLeft: '11px' }}
        >
          <span className="blue">{descriptionHead || '-'}</span>{' '}
          {descriptionTail || '-'}
        </td>
        <td width="30%">
          {row && row.materialNumber ? row.materialNumber : '-'}
        </td>
      </tr>
    );
  };

  const renderReadyForDropRow = index => {
    const row = readyForDropRowsData.data[index];
    const [descriptionHead, descriptionTail] = splitDescription(
      row && row.materialDescription,
    );

    const position =
      row && row.position ? row.position : READY_FOR_DROP_ROWS_COUNT - index;

    const conditionalBorderClass =
      index + 1 > 0 && (index + 1) % 5 === 0 && 'borderBt';
    return (
      <tr className="borderBt" key={index} data-testid={`select-row-${index}`}>
        <td
          className={conditionalBorderClass}
          width="7%"
          height="30"
          align="center"
          valign="middle"
        >
          {index === READY_FOR_DROP_ROWS_COUNT - 1 ? 'LineUp' : <>&nbsp;</>}
        </td>
        <td
          className={conditionalBorderClass}
          width="5%"
          height="30"
          align="center"
          valign="middle"
        >
          {getPaddedPositionString(position)}
        </td>
        <td
          className={conditionalBorderClass}
          width="13%"
          height="30"
          align="center"
          valign="middle"
          data-testid={`select-readyForDrop-asn-${index}`}
        >
          {getFormattedAsn(row)}
        </td>
        <td
          className={conditionalBorderClass}
          width="56%"
          align="left"
          valign="middle"
          style={{ paddingLeft: '11px' }}
        >
          <span className="blue">{descriptionHead || '-'}</span>{' '}
          {descriptionTail || '-'}
        </td>
        <td
          className={conditionalBorderClass}
          width="30%"
          align="left"
          valign="middle"
        >
          {row && row.materialNumber ? row.materialNumber : '-'}
        </td>
      </tr>
    );
  };

  return (
    <div>
      <div>
        <table
          width="98%"
          border="0"
          align="center"
          cellpadding="0"
          cellspacing="0"
          className="display_table"
        >
          <tr>
            <td>
              <table width="100%" border="0">
                <tr>
                  <td
                    width="24%"
                    height="72"
                    align="left"
                    valign="middle"
                    className="liveUpdate"
                  >
                    <span className="thead">LIVE UPDATE</span>
                    <strong className="date">
                      {moment().format('DD MMMM').toUpperCase()}{' '}
                      {moment().format('YYYY')}
                    </strong>{' '}
                  </td>
                  <td
                    width="36%"
                    align="left"
                    valign="middle"
                    className="title"
                  >
                    <span className="thead">Production Broadcast</span>
                    <strong>Frame Dropping Sequence</strong>
                  </td>
                  <td width="14%" align="left" valign="middle">
                    <span className="selectLine">Select Line</span>
                    <LineDropdownSelector isDarkStyle={true} />
                  </td>
                  <td
                    width="20%"
                    align="right"
                    valign="middle"
                    className="logo"
                  >
                    <Link to="/">
                      <img
                        src={Ipms}
                        alt="IPMS"
                        width="80%"
                        className="logo_img"
                      />
                    </Link>
                  </td>
                </tr>
              </table>
              <table
                width="100%"
                align="left"
                valign="top"
                cellPadding="0"
                cellSpacing="0"
              >
                <tr>
                  <td
                    colspan="4"
                    align="left"
                    valign="middle"
                    className="droprow"
                  >
                    Ready For Drop
                  </td>
                </tr>
                <table width="100%" align="left">
                  <tr className="asnHeading">
                    <td width="7%" align="left" valign="middle">
                      &nbsp;
                    </td>
                    <td width="5%" align="left" valign="middle">
                      &nbsp;
                    </td>
                    <td width="13%" height="30" align="center" valign="middle">
                      ASN
                    </td>
                    <td
                      width="56%"
                      align="left"
                      valign="middle"
                      style={{ paddingLeft: '5px' }}
                    >
                      VC Description
                    </td>
                    <td width="30%" align="left" valign="middle">
                      VC
                    </td>
                  </tr>
                </table>
                <tr>
                  <td align="left" valign="top">
                    <table
                      width="100%"
                      border="0"
                      cellspacing="0"
                      cellpadding="0"
                      className="inner-table"
                      data-testid={`select-row-table`}
                    >
                      {[...Array(READY_FOR_DROP_ROWS_COUNT).keys()].map(index =>
                        renderReadyForDropRow(index),
                      )}
                    </table>
                  </td>
                </tr>

                <tr>
                  <td
                    colspan="4"
                    align="left"
                    valign="middle"
                    className="droprow"
                    style={{ color: '#e8f1ed', background: '#01653c' }}
                  >
                    DROPPED CHASSIS
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <tr>
            <td align="left" valign="top">
              <table width="100%" border="0" cellspacing="0" cellpadding="0">
                <tr>
                  <td>
                    <table
                      width="100%"
                      border="0"
                      align="center"
                      cellpadding="0"
                      cellspacing="0"
                      className="inner-table"
                    >
                      {[...Array(DROPPED_ROWS_COUNT).keys()].map(renderRow)}
                    </table>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <tr align="center" valign="middle">
            <td
              height="60"
              style={{ fontSize: '0.5em', borderTop: 'solid 1px #4d5053' }}
            >
              Last Refreshed:{' '}
              {lastRefreshedDate
                ? lastRefreshedDate.format('hh:mmA [(IST)] DD MMMM YYYY')
                : '--:--'}
            </td>
          </tr>
        </table>
      </div>
    </div>
  );
};

export default InformationRadiator;
